import { useState, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { updateEntry } from 'apps/formsCreationKit/store/actions/sharedActions'
import { ClearAll } from 'apps/studio/components/choices/ClearAll'
import { ShowChoice } from 'apps/studio/components/choices/ShowChoice'
import { CommonListChoices } from 'apps/studio/components/choices/CommonListChoices'
import { ReferenceDataPreview } from 'apps/studio/components/choices/ReferenceDataPreview'
import { ChoiceOption } from 'apps/studio/components/choices/ChoiceOption'
import { lib } from '@gocanvas/client'
const { SINGLE_CHOICE_BOXES_STYLE, SINGLE_CHOICE_DUPLICATE_STYLES } = lib

export const newBoxDefaultEntries = (
  selectedMaxNum,
  entry_values,
  focusIndex,
  setFocusIndex
) => {
  const entriesNum = selectedMaxNum - entry_values.length
  if (entriesNum >= 0) {
    const totalEntries = [...Array(entriesNum)].map(() => ({}))
    if (focusIndex && setFocusIndex) {
      setFocusIndex(0)
    }
    return [...entry_values, ...totalEntries].map((entry, i) => {
      return { ...entry, position: i } //add positions
    })
  } else {
    return entry_values.slice(0, entriesNum)
  }
}

export const AddChoices = ({
  entry_values = [],
  guid = '',
  choicesLabel = 'Choices',
  default: defaultValue,
  referenceDataOptions = [],
  style = 1,
  type,
  minimum = 2,
  maximum = 5,
  listType,
  ...entry
}) => {
  const [focusIndex, setFocusIndex] = useState(null)
  const [optionsType, setOptionsType] = useState(false)
  const dispatch = useDispatch()
  const { reference_data_id, reference_data_column, data_source_context_id, live_data_source_column } = entry
  const showReferenceData = reference_data_id != null || data_source_context_id != null
  const showChoice = !showReferenceData && !entry_values.length && !optionsType
  const showDuplicates =
    type == 'Dropdown' &&
    SINGLE_CHOICE_DUPLICATE_STYLES.includes(parseInt(style, 10))
  const showCommonChoices =
    optionsType == 'edit-common-list' &&
    !showReferenceData &&
    !entry_values.length
  const showTypeMyOwn = !showChoice && !showReferenceData && !showCommonChoices

  const newDefaultEntries = () => {
    if (focusIndex !== 0) {
      setFocusIndex(0)
    }
    return [{ position: 0 }]
  }

  const options =
    entry_values.length > 0
      ? entry_values
      : style == SINGLE_CHOICE_BOXES_STYLE
      ? newBoxDefaultEntries(maximum, entry_values, focusIndex, setFocusIndex)
      : newDefaultEntries()

  const bulkUpdateOptions = options => {
    dispatch(
      updateEntry({
        guid,
        entry_values: options,
      })
    )
  }

  const deleteOptionAt = position => {
    if (entry_values.length > 1) {
      setFocusIndex(position - 1)

      const otherOptions = options.reduce((acc, option, i) => {
        if (option.position != position) {
          acc.push({ ...option, position: acc.length })
        }
        return acc
      }, [])

      dispatch(
        updateEntry({
          guid,
          entry_values: otherOptions,
        })
      )
    }
  }

  // Enter key was pressed and blur was already canceled
  // update current line & determine
  // if a new line now needs to be created after the index
  const addNewOptionAfter = index => inputValue => {
    const createNewLine =
      options[index + 1]?.text || index == options.length - 1
    if (
      createNewLine &&
      (style != SINGLE_CHOICE_BOXES_STYLE || options.length < maximum)
    ) {
      // insert new option after current index
      let updatedOptions = [
        ...options.slice(0, index),
        { ...options[index], text: inputValue },
        { position: index + 1 },
        ...options.slice(index + 1),
      ]
      // fix positions
      updatedOptions = updatedOptions.map((item, i) => ({
        ...item,
        position: i,
      }))
      dispatch(
        updateEntry({
          guid,
          entry_values: updatedOptions,
        })
      )
    } else {
      // update just the current line on enter
      let updatedOptions = [
        ...options.slice(0, index),
        { ...options[index], text: inputValue },
        ...options.slice(index + 1),
      ]
      dispatch(
        updateEntry({
          guid,
          entry_values: updatedOptions,
        })
      )
    }
    setFocusIndex(index + 1)
  }

  const onPaste = position => event => {
    const { value } = event.target
    const hasValue = String(value).length //not trimmed, allowing whitespace option
    const offset = hasValue ? 1 : 0 //paste into new lines unless current is empty
    const pastedText = event.clipboardData.getData('text')

    // only override the default behavior if there are newlines
    if (pastedText && pastedText.includes('\n')) {
      const pastedLines = pastedText.split(/\n+/).map(item => ({
        text: item.trim(),
      }))
      const newOptionsLn = pastedLines.length

      let combinedLines = [...options]
      if (!hasValue) {
        combinedLines = combinedLines
          .slice(0, position)
          .concat(pastedLines, combinedLines.slice(position + 1))
      } else {
        combinedLines.splice(position + offset, 0, ...pastedLines)
      }
      // only apply the field maximum to the boxes style
      if (type === 'Single Choice' && style == SINGLE_CHOICE_BOXES_STYLE) {
        combinedLines.length = Math.min(combinedLines.length, maximum)
      }
      // 15 really should be the field default, per builder v2
      // if (type === 'Multiple Choice'){
      //   combinedLines.length = Math.min(combinedLines.length, 15)
      // }
      // reindex all positions
      bulkUpdateOptions(combinedLines.map((o, i) => ({ ...o, position: i })))
      setFocusIndex(position + offset + newOptionsLn - 1)
      event.preventDefault()
    }
  }

  const update = useCallback(
    position => value => {
      let updatedOption = {
        position,
        text: value,
      }
      const values = options.map(e =>
        e.position == position ? updatedOption : e
      )
      bulkUpdateOptions(values)
    },
    [options]
  )

  return (
    <div className="gcds--card choices--field">
      <div
        className={`field--options input--placeholder settings--choice-wrapper`}
      >
        {showChoice && (
          <ShowChoice setOptionsType={setOptionsType} guid={guid} />
        )}
        {showReferenceData && (
          <ReferenceDataPreview
            {...{
              reference_data_id,
              reference_data_column,
              data_source_context_id,
              live_data_source_column,
              showDuplicates,
            }}
          />
        )}
        {showCommonChoices && (
          <CommonListChoices guid={guid} style={style} maximum={maximum} />
        )}
        {showTypeMyOwn && (
          <div
            className={`choice--options ${
              style == SINGLE_CHOICE_BOXES_STYLE ? 'no-scroll' : ''
            }`}
          >
            {options.map((option = {}, i) => {
              return (
                <ChoiceOption
                  {...option}
                  key={`${guid}-option-${option.position || i}`}
                  index={i}
                  shouldGetFocus={i === focusIndex}
                  setFocusIndex={setFocusIndex}
                  onChange={update(i)}
                  onDelete={() => deleteOptionAt(i)}
                  onPaste={onPaste(i)}
                  onEnter={addNewOptionAfter(i)}
                />
              )
            })}
          </div>
        )}
      </div>
      {entry_values.length > 1 && !reference_data_id && (
        <div className="choices--controls">
          <ClearAll
            style={style}
            maximum={maximum}
            guid={guid}
            setOptionsType={setOptionsType}
          />
        </div>
      )}
    </div>
  )
}
